/**
 * Utility functions for converting between snake_case (database) and camelCase (JavaScript)
 *
 * Copied from admin/src/utils/api.ts to maintain consistency across frontends
 */

/**
 * Convert snake_case object keys to camelCase recursively
 *
 * Special handling for PRESERVE_KEYS_FIELDS:
 * - 'pages': Preserves page IDs as keys (e.g., "start", "page_two")
 * - 'workflowLayout': Preserves workflow node IDs
 *
 * @param obj - Object with snake_case keys
 * @param parentKey - Parent key name (for tracking context)
 * @returns Object with camelCase keys
 */
export const toCamelCase = (obj: any, parentKey: string = ''): any => {
  if (Array.isArray(obj)) {
    return obj.map(item => toCamelCase(item, ''));
  } else if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj).reduce((acc, key) => {
      const camelKey = key.replace(/_([a-z])/g, (g) => g[1].toUpperCase());

      // Fields where inner keys should be preserved (page IDs, workflow node IDs)
      const PRESERVE_KEYS_FIELDS = ['pages', 'workflowLayout'];

      if (PRESERVE_KEYS_FIELDS.includes(camelKey) && typeof obj[key] === 'object' && !Array.isArray(obj[key])) {
        // Preserve inner keys (page IDs), but convert their content recursively
        acc[camelKey] = Object.keys(obj[key]).reduce((innerAcc, innerKey) => {
          innerAcc[innerKey] = toCamelCase(obj[key][innerKey], innerKey);
          return innerAcc;
        }, {} as any);
      } else {
        // Normal conversion for all other fields
        acc[camelKey] = toCamelCase(obj[key], camelKey);
      }

      return acc;
    }, {} as any);
  }
  return obj;
};

/**
 * Convert camelCase object keys to snake_case recursively
 *
 * Special handling for PRESERVE_KEYS_FIELDS (same as toCamelCase)
 *
 * @param obj - Object with camelCase keys
 * @param parentKey - Parent key name (for tracking context)
 * @returns Object with snake_case keys
 */
export const toSnakeCase = (obj: any, parentKey: string = ''): any => {
  if (Array.isArray(obj)) {
    return obj.map(item => toSnakeCase(item, ''));
  } else if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj).reduce((acc, key) => {
      const snakeKey = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

      const PRESERVE_KEYS_FIELDS = ['pages', 'workflowLayout'];

      if (PRESERVE_KEYS_FIELDS.includes(key) && typeof obj[key] === 'object' && !Array.isArray(obj[key])) {
        // Preserve inner keys, convert content recursively
        acc[snakeKey] = Object.keys(obj[key]).reduce((innerAcc, innerKey) => {
          innerAcc[innerKey] = toSnakeCase(obj[key][innerKey], innerKey);
          return innerAcc;
        }, {} as any);
      } else {
        // Normal conversion
        acc[snakeKey] = toSnakeCase(obj[key], snakeKey);
      }

      return acc;
    }, {} as any);
  }
  return obj;
};
